<template>
    <div id="modal-inspection-form" class="modal fade pt-10" tabindex="-1" role="dialog"
         aria-labelledby="inspectionCreateFormModal"
         aria-hidden="true"
         style="display: none;">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title font-normal" id="modalTitle">New Inspection</h4>
                    <button class="close" @click="hideInspectionCreateForm()" aria-hidden="true">×</button>
                </div>
                <div class="modal-body">

                    <!-- Inspection Detail Section -->
                    <div class="form" v-show="section === 0">
                        <!-- Asset -->
                        <div class="form-group">
                            <label class="font-normal">Asset
                                <el-popover
                                    class="inline"
                                    v-if="selectedAsset && selectedAsset.notes"
                                    placement="right"
                                    trigger="click"
                                    width="400">
                                    <div>
                                        <h4 class="font-bold">Asset Notes</h4>
                                        <p>{{ selectedAsset.notes }}</p>
                                    </div>
                                    <i class="mdi mdi-comment-text cursor-pointer text-md" slot="reference"></i>
                                </el-popover>
                            </label>

                            <div class="row">
                                <div class="col-sm-12">
                                    <remote-select
                                        v-if="!todoId"
                                        v-model="selectedAsset"
                                        @focus="searchAssets"
                                        :remote-method="searchAssets"
                                        :loading="loading"
                                        :data="assets"
                                        item-label="name"
                                        placeholder="Start typing to search assets, addresses or linked contacts">
                                        <template slot-scope="props">
                                            <assets-drop-down-item :asset="props.item"></assets-drop-down-item>
                                        </template>
                                    </remote-select>

                                    <!-- When creating inspection from todos, we already have the asset-->
                                    <div v-else>{{ selectedAsset.name }}</div>

                                    <span class="invalid-feedback d-block"
                                          v-show="form.errors.has('asset_id')">
                                        {{ form.errors.get("asset_id") }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Contact Group -->
                        <div class="form-group" v-if="showTenantField && !this.selectedTenant && hasFeature('feat_leases')">
                            <label class="font-normal input-label block">Select Lease</label>
                            <remote-select
                                v-model="selectedContactGroup"
                                @focus="fetchContactGroups"
                                :remote-method="fetchContactGroups"
                                :loading="loading"
                                :data="contactGroups"
                                item-label="compiled_label"
                                placeholder="Select Lease">

                                <template slot-scope="props">
                                    <div class="w-full max-w-0 text-sm font-medium text-gray-900 leading-snug py-3">
                                        <div class="font-semibold flex items-center justify-between">
                                            <div>
                                                {{ props.item.start_date | date }}
                                                {{ props.item.end_date ? "to " + $options.filters.date(props.item.end_date) : "to no end date" }}
                                            </div>
                                            <div class="mt-1 truncate text-indigo-600 font-bold">{{ props.item.secondary_identifier_id }}</div>
                                        </div>
                                        <div v-if="props.item.contacts && props.item.contacts.length > 0">
                                            <ul class="text-gray-400 font-normal">
                                                <li v-for="contact in props.item.contacts" class="flex items-center">
                                                    <icon-user class="w-5 h-5 inline-block pr-2 pt-1" :filled="contact.pivot.primary"></icon-user>
                                                    <span class="text-sm">{{ contact.full_name }}</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </template>
                            </remote-select>
                            <span class="invalid-feedback" :class="{'d-block': form.errors.has('contact_group_id')}"
                                  v-show="form.errors.has('contact_group_id')">
                                {{ form.errors.get("contact_group_id") }}
                            </span>
                        </div>

                        <!--Individual Tenant Selection-->
                        <div class="form-group" v-if="showTenantField && !this.selectedContactGroup">
                            <label class="font-normal input-label block">Tenant</label>
                            <remote-select
                                v-model="selectedTenant"
                                @focus="fetchTenants"
                                :remote-method="fetchTenants"
                                :loading="loading"
                                :data="tenants"
                                item-label="full_name"
                                placeholder="Start typing to search tenants">
                                <template slot-scope="props">
                                    <div class="flex items-center">
                                        <span>{{ props.item.full_name }}</span>
                                        <div class="text-xs">
                                            <span class="text-gray-500 bg-gray-200 ml-2 rounded-lg px-2 " v-if="props.item.archived_at !== null">Archived</span>
                                        </div>
                                    </div>
                                </template>
                            </remote-select>
                            <span class="invalid-feedback" :class="{'d-block': form.errors.has('tenant_id')}"
                                  v-show="form.errors.has('tenant_id')">
                                {{ form.errors.get("tenant_id") }}
                            </span>
                        </div>

                        <!-- Date & Time -->
                        <div class="form-group">
                            <label class="font-normal mb-0">Date</label>
                            <div id="date_time" class="row">
                                <div class="col-sm-6 mt-1">
                                    <el-date-picker
                                        @change="inputChanged"
                                        v-model="inspection.date"
                                        type="date"
                                        placeholder="Pick a day"
                                        value-format="yyyy-MM-dd">
                                    </el-date-picker>
                                    <span class="invalid-feedback" :class="{'d-block': form.errors.has('date')}"
                                          v-show="form.errors.has('date')">
                                        {{ form.errors.get("date") }}
                                    </span>
                                </div>
                                <div class="col-sm-6 mt-1">
                                    <el-time-select
                                        @change="inputChanged"
                                        v-model="inspection.time"
                                        :picker-options="{start: '06:00', step: '00:30',end: '24:00'}"
                                        placeholder="Select time">
                                    </el-time-select>
                                    <span class="invalid-feedback" :class="{'d-block': form.errors.has('time')}"
                                          v-show="form.errors.has('time')">
                                        {{ form.errors.get("time") }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Type -->
                        <div class="form-group">
                            <label class="font-normal">Type</label>
                            <div id="type">
                                <el-select v-model="inspection.type" placeholder="Select type">
                                    <el-option
                                        @change="inputChanged"
                                        v-for="type in inspectionTypes"
                                        :key="type"
                                        :label="type"
                                        :value="type">
                                    </el-option>
                                </el-select>
                            </div>

                            <span class="invalid-feedback" :class="{'d-block': form.errors.has('type')}"
                                  v-show="form.errors.has('type')">
                                {{ form.errors.get("type") }}
                            </span>
                        </div>

                        <!-- Inspector -->
                        <div class="form-group" v-if="this.selectedAsset">
                            <label class="font-normal">Inspector</label>

                            <remote-select
                                class="max-auto"
                                v-model="selectedInspector"
                                :key="selectedAsset ? selectedAsset.id : 'default'"
                                @focus="fetchInspectors"
                                :remote-method="fetchInspectors"
                                :loading="loading"
                                :ignore-user-portfolio-access="false"
                                :data="mutableInspectors"
                                item-label="name"
                                placeholder="Start typing to search users">
                                <template slot-scope="props">
                                    <div :class="{'opacity-50 cursor-not-allowed': !props.item.user_portfolio_access}">
                                        <div class="flex flex-wrap flex-col md:justify-between leading-none py-4">
                                            <div class="flex justify-between">
                                                <div>
                                                    {{ props.item.name }}
                                                </div>
                                                <div v-if="props.item.role_display" class="text-gray-400 text-xs mt-1 self-end">
                                                    {{ props.item.role_display }}
                                                </div>
                                            </div>
                                            <div class="flex flex-wrap flex-col gap-2 ">
                                                <span v-if="!props.item.user_portfolio_access" class="text-red-500 text-xs mt-1">
                                                    Inspector does not have access to asset’s portfolio.
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </remote-select>

                            <span class="invalid-feedback d-block"
                                  v-show="form.errors.has('inspector_id')">
                                {{ form.errors.get("inspector_id") }}
                            </span>
                        </div>
                    </div>

                    <!-- Inspection Starting Point Section -->
                    <div class="form" v-if="section === 1">
                        <div class="form-group">
                            <!-- Start Clean -->
                            <div class="starting-point mb-4">
                                <label class="font-normal custom-control custom-radio">
                                    <input id="start-clean" name="starting_point" type="radio"
                                           v-model="form.starting_point"
                                           value="clean"
                                           class="custom-control-input">
                                    <span class="custom-control-label">Start clean
                                        <small class="muted pull-left w-100">Start your inspection from scratch</small>
                                    </span>
                                </label>
                            </div>

                            <!-- Use Template -->
                            <div class="starting-point mb-4">
                                <label class="font-normal custom-control custom-radio">
                                    <input id="use-template" name="starting_point" type="radio"
                                           value="template"
                                           v-model="form.starting_point"
                                           class="custom-control-input">
                                    <span class="custom-control-label">Use template
                                        <small class="muted pull-left w-100">Select a template for your inspection</small>
                                    </span>
                                </label>

                                <div class="form-group mt-4" v-if="form.starting_point === 'template'">
                                    <div id="template">
                                        <el-select class="w-100" v-model="form.template_id"
                                                   placeholder="Search templates: eg. Bedroom" filterable>
                                            <el-option
                                                class="checklist-set-dropdown"
                                                v-for="template in templates"
                                                :key="template.id"
                                                :label="template.name"
                                                :value="template.id">
                                                <div>
                                                    <span style="line-height: 16px !important;">{{ template.name }}</span>
                                                    <div class="checklist-set-items">
                                                        <span v-for="area in template.areas"
                                                              :key="area.id"
                                                              class="label label-light-primary mr-1"
                                                              style="line-height: 14px !important;">{{ area.name }}
                                                        </span>
                                                    </div>

                                                </div>
                                            </el-option>
                                        </el-select>
                                    </div>

                                    <span class="invalid-feedback"
                                          :class="{'d-block': form.errors.has('template_id')}"
                                          v-show="form.errors.has('template_id')">
                                        {{ form.errors.get("template_id") }}
                                    </span>
                                </div>
                            </div>

                            <!-- Use Previous Inspection -->
                            <div class="starting-point mb-4">
                                <label class="font-normal custom-control custom-radio">
                                    <input id="use-inspection" name="starting_point" type="radio"
                                           v-model="form.starting_point"
                                           value="previous inspection"
                                           class="custom-control-input">
                                    <span class="custom-control-label">Use previous inspection
                                        <small class="muted pull-left w-100">Select another inspection for this asset as a starting point</small>
                                    </span>
                                </label>

                                <div class="form-group mt-4" v-if="form.starting_point === 'previous inspection'">
                                    <div id="template">
                                        <el-select class="w-100" v-model="form.previous_inspection_id"
                                                   placeholder="Select inspection">
                                            <el-option
                                                v-for="inspection in previousInspections"
                                                :key="inspection.id"
                                                :label="inspection.type + ' Inspection ' + inspection.date"
                                                :value="inspection.id">
                                            </el-option>
                                        </el-select>
                                    </div>
                                    <span class="invalid-feedback"
                                          :class="{'d-block': form.errors.has('previous_inspection_id')}"
                                          v-show="form.errors.has('previous_inspection_id')">
                                        {{ form.errors.get("previous_inspection_id") }}
                                    </span>
                                </div>

                                <div class="form-group mt-3" v-if="form.starting_point === 'previous inspection'">
                                    <label class="font-normal custom-control custom-checkbox pl-4 pr-4">
                                        <input type="checkbox" class="custom-control-input"
                                               v-model="form.copy_structure_only">
                                        <span class="custom-control-label">Structure Only
                                            <small
                                                class="text-muted"><br/>Item conditions and comments won't be copied from the selected inspection to the new inspection.</small>
                                        </span>
                                    </label>
                                </div>

                                <div class="form-group"
                                     v-if="form.starting_point === 'previous inspection' && !form.copy_structure_only">
                                    <label class="font-normal custom-control custom-checkbox pl-4 pr-4">
                                        <input type="checkbox" class="custom-control-input"
                                               v-model="form.copy_previous_photos">
                                        <span class="custom-control-label">Copy Photos
                                            <small class="text-muted"><br/>Include photos from the selected inspection in the new inspection.</small>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <button v-if="section === 1" class="btn btn-success waves-effect waves-light mr-auto"
                            @click.prevent="section = 0"
                            :disabled="form.busy">Back
                    </button>
                    <button v-if="section === 1" class="btn btn-success waves-effect waves-light"
                            @click.prevent="create"
                            :disabled="form.busy">
                        {{ form.busy ? "Creating..." : "Create" }}
                    </button>
                    <button v-if="section === 0"
                            class="btn btn-success waves-effect waves-light"
                            @click.prevent="nextSection"
                            :disabled="form.busy">Next
                    </button>
                    <button class="btn btn-default waves-effect" @click="hideInspectionCreateForm()"
                            :disabled="form.busy">Cancel
                    </button>
                </div>
            </div>
            <!-- /.modal-content -->
        </div>
        <!-- /.modal-dialog -->
    </div>
</template>

<script>
import AssetSearch from "../../mixins/asset-search";
import Inspectors from "../../mixins/inspectorAccess.js";


export default {
    name: "InspectionCreateForm",

    mixins: [AssetSearch, Inspectors],

    props: {
        inspectionTypes: {
            type: Array
        },
        asset: {
            type: Object
        },
        fromAsset: {
            type: Boolean,
            required: false,
        },
        todoId: {
            required: false,
            description: "Link inspection to todo when creating from todo"
        },
    },

    data () {
        return {
            inspection: {},
            selectedAsset: null,
            selectedInspector: null,
            selectedTenant: null,
            selectedContactGroup: null,
            form: new AppForm({
                starting_point: "clean"
            }),
            inspectors: [],
            section: 0,
            tenants: [],
            contactGroups: [],
            templates: [],
            previousInspections: [],
        };
    },

    mounted () {
        let self = this;
        Bus.$on("inspection-created", function (inspection = null) {
            self.hideInspectionCreateForm(inspection);
            Vue.nextTick(() => {
                self.$emit("inspection-created");
            });
        });
    },

    watch: {
        asset (newAsset) {
            this.selectedAsset = newAsset;
        },

        selectedAsset (newValue) {
            this.tenants = [];
            this.previousInspections = [];
            this.selectedTenant = null;
            this.selectedContactGroup = null;
            this.selectedInspector = null; // Reset the inspector when the asset changes
            this.mutableInspectors= [];


            if (newValue) {
                this.fetchTenants();
                this.fetchPreviousInspections();
                this.fetchInspectors(); // Fetch inspectors when the asset changes
            }
        }

    },

    computed: {
        showTenantField () {
            return this.selectedAsset !== null;
        },
    },

    methods: {
        showInspectionCreateForm () {
            if (this.verifyTeamIsSubscribed()) {

                this.fetchTemplates();

                if (!_.isEmpty(this.asset)) {
                    this.selectedAsset = this.asset;
                }
                $("#modal-inspection-form").modal("show");
            }
        },

        hideInspectionCreateForm (inspection = null) {
            this.$emit("close", inspection);
            $("#modal-inspection-form").modal("hide");
        },

        create () {
            this.form.asset_id = this.selectedAsset.id;
            this.form.date = this.inspection.date;
            this.form.time = this.inspection.time;
            this.form.type = this.inspection.type;
            this.form.inspector_id = this.selectedInspector.id;
            this.form.tenant_id = this.selectedTenant ? this.selectedTenant.id : null;
            this.form.contact_group_id = this.selectedContactGroup ? this.selectedContactGroup.id : null;
            this.form.todo_id = this.todoId ? this.todoId : null;

            App.post("/inspections/create", this.form)
            .then((response) => {
                this.resetData();
                window.toastr.success("Inspection created!");
                if (this.fromAsset) {
                    this.$emit("created-from-asset", response);
                    return;
                }
                Bus.$emit("inspection-created", response);
            })
            .catch(error => {
                if (this.form.errors.has("date")) {
                    this.section = 0;
                }
            });
        },

        fetchTemplates () {
            axios.get("/settings/inspections/templates/all")
            .then(response => {
                this.templates = response.data;
            })
            .catch(() => {
                toastr.errors("Error loading templates!");
            });
        },

        fetchTenants: _.debounce(function (value) {
            this.loading = true;

            axios.get("/contacts/asset/" + this.selectedAsset.id, {
                params: {
                    filter: value,
                    archived: false,
                    role: "tenant"
                }
            })
            .then(response => {
                this.loading = false;
                if (this.hasFeature("release_view_asset_rework")) {
                    this.tenants = response.data.data;
                } else {
                    this.tenants = response.data;
                }

            })
            .catch(errors => {
                this.loading = false;
                toastr.error(errors.message);
            });
        }, 300),

        fetchContactGroups: _.debounce(function (value) {
            this.loading = true;

            axios.get("/contact-groups/asset/" + this.selectedAsset.id, {})
            .then(response => {
                this.loading = false;
                this.contactGroups = response.data;
            })
            .catch(errors => {
                this.loading = false;
                toastr.error(errors.message);
            });
        }, 300),

        fetchPreviousInspections () {
            if (this.selectedAsset === null || !this.verifyTeamIsSubscribed()) {
                return;
            }

            axios.get("/inspections/" + this.selectedAsset.id + "/previous")
            .then(response => {
                this.previousInspections = response.data;
            })
            .catch(() => {
                toastr.error("Error loading previous inspections!");
            });
        },

        resetData () {
            this.form = new AppForm({
                starting_point: "clean"
            });
            this.selectedAsset = null;
            this.selectedInspector = null;
            this.inspection = {};
            this.selectedTenant = null;
            this.selectedContactGroup = null;
            this.section = 0;
        },

        inputChanged () {
            this.form.errors.forget();
        },

        nextSection () {
            let errors = {};

            if (!this.selectedAsset) {
                errors.asset_id = ["Select an " + App.lang.assetName + " to continue."];
            }

            if (!this.inspection.date) {
                errors.date = ["The date field is required"];
            }
            if (!this.inspection.time) {
                errors.time = ["The time field is required"];
            }
            if (!this.inspection.type) {
                errors.type = ["The inspection type field is required."];
            }
            if (!this.selectedInspector) {
                errors.inspector_id = ["Select inspector to continue."];
            }

            if (!_.isEmpty(errors)) {
                this.form.errors.set(errors);
            } else {
                this.section = 1;
            }
        },
    },
};
</script>
